import { useState } from "react";
import { Button, Icon, SelectInput, Spinner, Text, Title } from "components/common";
import PageLayout from "components/common/pageLayout/pageLayout";
import { useNavigate } from "react-router-dom";
import { UserAvatar } from "components/custom/userListRow";
import DemoDataToggle from "components/custom/demoDataToggle";
import { useGetLeaderboardQuery } from "features/community/apis";
import { getLeaderboardDemoData } from "features/community/mockData/leaderboard";
import { getUserProfilePath } from "constants/app-paths";
import styles from "./Leaderboard.module.less";

const SCOPES = [
  { key: "following", label: "Following" },
  { key: "global", label: "Global" },
];

const METRICS = [
  { key: "questions", label: "Questions" },
  { key: "accuracy", label: "Accuracy" },
  { key: "streak", label: "Streak" },
];

const TIMEFRAMES = [
  { key: "all", label: "All time" },
  { key: "month", label: "Month" },
  { key: "week", label: "Week" },
];

const PODIUM = {
  1: {
    label: "1st",
    title: "Top performer",
    className: "podiumGold",
    medalClassName: "medalGold",
  },
  2: {
    label: "2nd",
    title: "Runner up",
    className: "podiumSilver",
    medalClassName: "medalSilver",
  },
  3: {
    label: "3rd",
    title: "Third place",
    className: "podiumBronze",
    medalClassName: "medalBronze",
  },
};

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatValue = (value, metric) => {
  if (metric === "accuracy") return `${Number(value || 0)}%`;
  if (metric === "streak") {
    const days = Number(value || 0);
    return `${formatNumber(days)} day${days === 1 ? "" : "s"}`;
  }
  return formatNumber(value);
};

const metricLabel = (metric) => {
  if (metric === "accuracy") return "Accuracy";
  if (metric === "streak") return "Day streak";
  return "Questions answered";
};

const topMetricLabel = (metric) => {
  if (metric === "accuracy") return "Best accuracy";
  if (metric === "streak") return "Longest streak";
  return "Most questions";
};

const getLearningIdentity = (row) =>
  [row.educationLevel, ...(row.specialties || []).slice(0, 1)]
    .filter(Boolean)
    .join(" · ");

const weeklyActivityLabel = (row) => {
  const total = Number(row.questions_this_week || 0);
  return `${formatNumber(total)} question${total === 1 ? "" : "s"} this week`;
};

const getCountdownLabel = (dateString) => {
  const target = dateString ? new Date(dateString).getTime() : 0;
  if (!target) return "This week";

  const diff = Math.max(target - Date.now(), 0);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);

  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  return `${hours}h ${minutes}m`;
};

const getRowChangeMeta = (row) => {
  const change =
    row.rank_change !== null && row.rank_change !== undefined
      ? row.rank_change
      : row.metric_delta;

  if (change === null || change === undefined) {
    return { label: "-", type: "neutral", icon: null };
  }
  if (change > 0) {
    return { label: formatNumber(change), type: "positive", icon: "up" };
  }
  if (change < 0) {
    return {
      label: formatNumber(Math.abs(change)),
      type: "negative",
      icon: "down",
    };
  }
  return { label: "0", type: "neutral", icon: null };
};

const ChipRow = ({ options, value, onChange }) => (
  <div className={styles.chipRow}>
    {options.map((option) => (
      <Button
        key={option.key}
        size="small"
        type="default"
        className={`${styles.filterChip} ${
          value === option.key ? styles.filterChipActive : ""
        }`}
        text={option.label}
        onClick={() => onChange(option.key)}
      />
    ))}
  </div>
);

// `withIcon` is only true inside the podium cards; table rows use a plain
// text pill with no leading icon, matching the design.
const RoleBadge = ({ badge, withIcon = false }) => {
  if (!badge) return null;
  const isEducator = badge === "Educator";

  return (
    <span className={`${styles.roleBadge} ${isEducator ? styles.educator : styles.learner}`}>
      {withIcon && <Icon icon="AiOutlineBook" size={13} />}
      {badge}
    </span>
  );
};

// Shared ribbon-medal used at both the small (table row) and large (podium)
// sizes. Fixed pixel dimensions per size variant — deliberately not em-based,
// since inherited font-size varies by where the medal is mounted.
const RankMedal = ({ rank, size = "sm" }) => {
  const podium = PODIUM[rank];
  const wrapClass = size === "lg" ? styles.medalWrap : styles.medalWrapSm;
  return (
    <span className={`${wrapClass} ${styles[podium.medalClassName]}`}>
      <span className={styles.medalCircle}>{rank}</span>
    </span>
  );
};

const RankBadge = ({ rank }) => {
  if (rank >= 1 && rank <= 3) {
    return <RankMedal rank={rank} size="sm" />;
  }
  return <span className={styles.rankNumber}>{rank}</span>;
};

const KpiCard = ({ icon, value, label, tone = "blue" }) => (
  <div className={`${styles.kpiCard} ${styles[tone]}`}>
    <div className={styles.kpiIcon}>
      <Icon icon={icon} size={20} />
    </div>
    <Text className={styles.kpiValue}>{value}</Text>
    <Text className={styles.kpiLabel}>{label}</Text>
  </div>
);

const SideIcon = ({ icon, tone = "purple" }) => (
  <span className={`${styles.sideIconBox} ${styles[tone]}`}>
    <Icon icon={icon} size={16} />
  </span>
);

const ProgressBar = ({ value, className = "" }) => (
  <div className={`${styles.progressTrack} ${className}`}>
    <span style={{ width: `${Math.max(0, Math.min(value || 0, 100))}%` }} />
  </div>
);

const Row = ({ row, metric, onClick }) => {
  const changeMeta = getRowChangeMeta(row);
  const learningIdentity = getLearningIdentity(row);

  return (
    <div
      onClick={onClick}
      className={`${styles.leaderboardRow} ${row.isMe ? styles.currentUser : ""}`}
    >
      <div className={styles.tableRank}>
        <RankBadge rank={row.rank} />
      </div>
      <div className={styles.tableUser}>
        <UserAvatar user={row} size={34} />
        <div className={styles.person}>
          <div className={styles.nameRow}>
            <Text className={styles.userName}>
              {row.name} {row.isMe ? "(You)" : ""}
            </Text>
            {row.is_verified && (
              <Icon icon="FiCheckCircle" className={styles.verifiedIcon} size={14} />
            )}
          </div>
          {(learningIdentity || row.stats?.totalQuizzes > 0) && (
            <Text className={styles.userMeta}>
              {learningIdentity || `${formatNumber(row.stats.totalQuizzes)} quizzes completed`}
            </Text>
          )}
        </div>
      </div>
      <div className={styles.tableRole}>
        <RoleBadge badge={row.badge} />
      </div>
      <div className={styles.tableMetric}>{formatValue(row.value, metric)}</div>
      <div className={`${styles.tableChange} ${styles[changeMeta.type]}`}>
        {changeMeta.icon === "up" && <span className={styles.changeArrow}>↑</span>}
        {changeMeta.icon === "down" && <span className={styles.changeArrow}>↓</span>}
        {changeMeta.label}
      </div>
    </div>
  );
};

const Leaderboard = () => {
  const navigate = useNavigate();
  const canUseDemoData = process.env.NODE_ENV !== "production";
  const [useDemoData, setUseDemoData] = useState(false);
  const [scope, setScope] = useState("global");
  const [metric, setMetric] = useState("questions");
  const [timeframe, setTimeframe] = useState("week");

  const { data, isFetching, isError, refetch } = useGetLeaderboardQuery(
    { scope, metric, timeframe },
    { skip: canUseDemoData && useDemoData, refetchOnMountOrArgChange: true }
  );

  const leaderboardData =
    canUseDemoData && useDemoData ? getLeaderboardDemoData(metric) : data;
  const rows = leaderboardData?.data || [];
  const myRank = leaderboardData?.myRank;
  const summary = leaderboardData?.summary || {};
  const challenge = leaderboardData?.challenge || {};
  const currentUser = rows.find((row) => row.isMe) || myRank;
  const activeMetric = METRICS.find((item) => item.key === metric)?.label;
  const topThree = [...rows]
    .filter((row) => row.rank >= 1 && row.rank <= 3)
    .sort((a, b) => a.rank - b.rank);
  const hasPodium = topThree.length > 0;
  const podiumMaxValue = Math.max(
    ...topThree.map((row) => Number(row.value) || 0),
    1
  );
  // Always preserve the podium positions. In a small live leaderboard this
  // prevents the first two cards from stretching into oversized half-width cards.
  const podiumRows = [
    topThree.find((row) => row.rank === 2),
    topThree.find((row) => row.rank === 1),
    topThree.find((row) => row.rank === 3),
  ].filter(Boolean);
  const myProgress =
    summary.total_ranked_users && summary.your_rank
      ? ((summary.total_ranked_users - summary.your_rank + 1) /
          summary.total_ranked_users) *
        100
      : summary.my_percentile || 0;
  const topPercent =
    summary.total_ranked_users && summary.your_rank
      ? Math.max(
          1,
          Math.ceil((summary.your_rank / summary.total_ranked_users) * 100)
        )
      : null;
  const challengeProgress = challenge.target
    ? (challenge.progress / challenge.target) * 100
    : 0;
  const leaderboardMetricLabel = topMetricLabel(metric);

  return (
    <PageLayout label={<Title level={2}>Community</Title>}>
      <div className={styles.leaderboardShell}>
        <div className={styles.leaderboardPage}>
          <div className={styles.pageHeader}>
            <div className={styles.headerIntro}>
              <Title level={3} className={styles.pageTitle}>Leaderboard</Title>
              <Text className={styles.pageDescription}>
                See how you compare with the IllnessRx community.
              </Text>
            </div>
            <div className={styles.headerControls}>
              <div className={styles.scopeControl}>
                <Text className={styles.controlLabel}>View</Text>
                <ChipRow options={SCOPES} value={scope} onChange={setScope} />
              </div>
              <div className={styles.selectControl}>
                <Text className={styles.controlLabel}>Rank by</Text>
                <SelectInput
                  className={styles.filterSelect}
                  options={METRICS}
                  value={metric}
                  suffixIcon="caret"
                  showSearch={false}
                  sortSelectedList={false}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.key}
                  onChange={setMetric}
                />
              </div>
              {metric !== "streak" && (
                <div className={styles.selectControl}>
                  <Text className={styles.controlLabel}>Timeframe</Text>
                  <SelectInput
                    className={styles.filterSelect}
                    options={TIMEFRAMES}
                    value={timeframe}
                    suffixIcon="caret"
                    showSearch={false}
                    sortSelectedList={false}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.key}
                    onChange={setTimeframe}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.contentGrid}>
            <main className={styles.mainColumn}>
              {hasPodium && (
                <div
                  className={`${styles.podium} ${
                    topThree.length === 1
                        ? styles.podiumSingle
                        : ""
                  }`}
                >
                  {podiumRows.map((row) => {
                    const podium = PODIUM[row.rank];
                    const progress = Math.round(
                      ((Number(row.value) || 0) / podiumMaxValue) * 100
                    );
                    const gapToFirst =
                      row.rank === 1
                        ? row.questions_this_week
                        : Math.max((topThree[0]?.value || 0) - (row.value || 0), 0);

                    return (
                      <div
                        key={row.user_code}
                        className={`${styles.podiumCard} ${styles[podium.className]}`}
                        onClick={() => navigate(getUserProfilePath(row.user_code))}
                      >
                        <div className={styles.podiumContent}>
                          <RankMedal rank={row.rank} size="lg" />
                          {row.rank === 1 && (
                            <Icon icon="CgCrown" className={styles.crown} size={30} />
                          )}
                          <div className={styles.podiumAvatar}>
                            <UserAvatar user={row} size={row.rank === 1 ? 62 : 58} />
                          </div>
                          <div className={styles.podiumIdentity}>
                            <div className={styles.podiumPerson}>
                              <Text className={styles.podiumName}>
                                {row.name}{row.isMe ? " (You)" : ""}
                              </Text>
                              <RoleBadge badge={row.badge} withIcon />
                            </div>
                            {getLearningIdentity(row) && (
                              <Text className={styles.podiumEducation}>
                                {getLearningIdentity(row)}
                              </Text>
                            )}
                            <Text className={styles.podiumValue}>
                              {formatValue(row.value, metric)}
                            </Text>
                          </div>
                          <div className={styles.podiumMetricArea}>
                            <Text className={styles.podiumMetricLabel}>
                              {metricLabel(metric)}
                            </Text>
                            {row.rank === 1 && (
                              <Text className={styles.podiumWeeklyChange}>
                                {row.questions_this_week > 0 ? "↑ " : ""}
                                {weeklyActivityLabel(row)}
                              </Text>
                            )}
                          </div>
                          {row.rank !== 1 && (
                            <ProgressBar value={progress} />
                          )}
                        </div>
                        <div className={styles.podiumFooter}>
                          <span>#{row.rank}</span>
                          <strong>{podium.title}</strong>
                          {row.rank !== 1 && <small>{formatNumber(gapToFirst)} behind #1</small>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {isFetching && rows.length === 0 ? (
                <div className={styles.loadingState}>
                  <Spinner />
                </div>
              ) : isError ? (
                <div className={styles.emptyState}>
                  <Text className="gray-800">Unable to load leaderboard.</Text>
                  <Button size="small" type="default" text="Retry" onClick={refetch} />
                </div>
              ) : rows.length === 0 ? (
                <div className={styles.emptyState}>
                  <Text className="gray-800">
                    {scope === "following"
                      ? "Follow people to build your leaderboard."
                      : "No leaderboard data yet."}
                  </Text>
                </div>
              ) : (
                <div className={styles.leaderboardCard}>
                  {isFetching && (
                    <div className={styles.refreshOverlay}>
                      <Spinner size="small" />
                    </div>
                  )}
                  <div className={styles.tableTitle}>Rankings</div>
                  <div className={styles.tableHeader}>
                    <span>Rank</span>
                    <span>User</span>
                    <span>Role</span>
                    <span>{activeMetric}</span>
                    <span>Change</span>
                  </div>
                  {rows.map((row) => (
                    <Row
                      key={row.user_code}
                      row={row}
                      metric={metric}
                      onClick={() => navigate(getUserProfilePath(row.user_code))}
                    />
                  ))}
                </div>
              )}

              {myRank && (!hasPodium || myRank.rank > 3) && (
                <div className={styles.myRank}>
                  <Text className={styles.myRankLabel}>Your position</Text>
                  <div className={styles.leaderboardCard}>
                    <Row
                      row={myRank}
                      metric={metric}
                      onClick={() => navigate(getUserProfilePath(myRank.user_code))}
                    />
                  </div>
                </div>
              )}
            </main>

            <aside className={styles.sidebar}>
              <div className={styles.statsGrid}>
                <KpiCard
                  icon="BiComment"
                  value={formatNumber(summary.questions_today)}
                  label="Questions Today"
                  tone="blue"
                />
                <KpiCard
                  icon="FiUsers"
                  value={formatNumber(summary.active_users)}
                  label="Active Users"
                  tone="purple"
                />
                <KpiCard
                  icon="FiArrowUpRight"
                  value={formatValue(summary.top_score || topThree[0]?.value || 0, metric)}
                  label={leaderboardMetricLabel}
                  tone="green"
                />
                <KpiCard
                  icon="AiOutlineStar"
                  value={summary.your_rank ? `#${summary.your_rank}` : "-"}
                  label="Your Rank"
                  tone="amber"
                />
              </div>

              <div className={styles.sideCard}>
                <div className={styles.sideHeader}>
                  <Text className={styles.sideTitle}>Your Progress</Text>
                  {currentUser && <span className={styles.youPill}>You</span>}
                </div>
                <div className={styles.progressStats}>
                  <div>
                    <Text className={styles.sideLabel}>Rank</Text>
                    <Text className={styles.sideNumber}>
                      {summary.your_rank ? `#${summary.your_rank}` : "-"}
                    </Text>
                  </div>
                  <div>
                    <Text className={styles.sideLabel}>{activeMetric}</Text>
                    <Text className={styles.sideNumber}>
                      {formatValue(currentUser?.value || 0, metric)}
                    </Text>
                  </div>
                </div>
                <Text className={styles.sideMuted}>
                  {topPercent
                    ? `You're in the top ${topPercent}% of the community!`
                    : "Keep practicing to appear in the rankings."}
                </Text>
                <ProgressBar value={myProgress} className={styles.progressBlue} />
                <Text className={styles.sideFooter}>
                  {summary.next_higher_rank
                    ? `${formatNumber(
                        summary.next_higher_rank.gap
                      )} ${metricLabel(metric).toLowerCase()} until Rank #${
                        summary.next_higher_rank.rank
                      }`
                    : "You're at the top of this view."}
                </Text>
              </div>

              <div className={styles.sideCard}>
                <div className={styles.sideHeader}>
                  <Text className={styles.sideTitle}>
                    <SideIcon icon="FiMonitor" tone="purple" /> Your week
                  </Text>
                </div>
                <Text className={styles.sideMuted}>
                  {challenge.description || "Your question progress this week"}
                </Text>
                <div className={styles.challengeLine}>
                  <ProgressBar value={challengeProgress} className={styles.progressPurple} />
                  <span>
                    {formatNumber(challenge.progress)} / {formatNumber(challenge.target || 50)}
                  </span>
                </div>
                <Text className={styles.sideFooter}>
                  <Icon icon="FiCalendar" size={14} /> Ends in {getCountdownLabel(challenge.ends_at)}
                </Text>
              </div>

              {timeframe !== "all" && <div className={styles.sideCard}>
                <div className={styles.sideHeader}>
                  <Text className={styles.sideTitle}>
                    <SideIcon icon="FiCalendar" tone="blue" /> Leaderboard resets in
                  </Text>
                </div>
                <Text className={styles.countdown}>
                  {getCountdownLabel(leaderboardData?.next_reset_at)}
                </Text>
                <Text className={styles.sideFooter}>
                  <Icon icon="FiArrowUpRight" size={14} /> Stay active and climb the ranks!
                </Text>
              </div>}
            </aside>
          </div>

          {canUseDemoData && <DemoDataToggle value={useDemoData} onChange={setUseDemoData} label="Leaderboard data" />}
        </div>
      </div>
    </PageLayout>
  );
};

export default Leaderboard;
